import SimpleSchema from 'simpl-schema';
import { values, isEmpty } from 'lodash';
import requiredIf from '../../modules/schema-conditional-required';
import { SUBTASK_PARAM_TYPES } from './taskConstants';

/**
 * A parameter for a subtask. Inconming task list.
 *
 * @typedef {object} SubtaskParameterIN
 * @property {string} id - The paramter id. UNIQUE for each node.
 * @property {string} label - A human readable name for the parameter.
 * @property {string} type - The parameter type. One of [STRING,NUMBER,BOOLEAN,DATETIME].
 * @property {boolean} required - Flag to signal that the parameter is required.
 * @property {Array} [options] - The options available for the parameter.
 * @property {boolean} [multiValue] - Flag that signals multiple options can be selected.
 * @property {string} comments - The comments for the parameter.
 */

/**
 * A parameter for a subtask.
 *
 * @typedef {object} SubtaskParameter
 * @augments SubtaskParameterIN
 * @property {string|string[]} value - The value for the parameter, selected by the user.
 */

const SubtaskParameterSchema = new SimpleSchema({
  id: {
    type: String,
    label: 'The paramter id. UNIQUE for each node'
  },
  label: {
    type: String,
    label: 'A human readable name for the parameter'
  },
  type: {
    type: String,
    label: 'The parameter type',
    allowedValues: values(SUBTASK_PARAM_TYPES)
  },
  required: {
    type: Boolean,
    label: 'Flag to signal that the parameter is required.'
  },
  options: {
    type: Array,
    label: 'The options available for the parameter',
    optional: true
  },
  'options.$': {
    type: String,
    label: 'An option value for the parameter'
  },
  multiValue: {
    type: Boolean,
    label: 'Flag that signals multiple options can be selected',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: !isEmpty(this.siblingField('options').value),
        schemaInstance: this
      });
    }
  },
  value: {
    type: SimpleSchema.oneOf(String, Array),
    label: 'The value for the parameter, selected by the user',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.siblingField('required').value === true,
        schemaInstance: this
      });
    }
  },
  'value.$': {
    type: String,
    label: 'One of the values selected by the user when multiValue',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.siblingField('multiValue').value === true,
        schemaInstance: this
      });
    }
  },
  comments: {
    type: String,
    label: 'The comments for the parameter',
    optional: true
  }
});

export default SubtaskParameterSchema;
